import { showToast } from './ui/toast.js';
import { TOASTS } from './config/toasts.js';
import { ANALYSIS_STATUS, UPLOAD_QUEUE_CONFIG } from './config/uploads.js';
import { UPLOAD_LIMITS } from './config/uploadLimits.js';
import { resizeImageToFile } from './upload_utils.js';
import { UI_COPY } from './config/uiCopy.js';
import * as API from './api.js';

const LIBRARY_UPLOAD_SESSION_STORAGE_KEY = 'mosaic_library_upload_session_id';

export class UploadQueue {
    constructor() {
        this.queue = [];
        this.activeUploads = 0;
        this.maxConcurrent = UPLOAD_QUEUE_CONFIG.MAX_CONCURRENT;
        this.libraryId = null;
        this.uploadUrl = "";
        this.sessionId = this._loadPersistedSessionId();
        this._activeUploadToast = null;
        this._abortBatch = false;
        this._abortReason = '';
        this._abortMeta = {};
        this._activeXhrs = new Set();
        this._sessionReleaseSent = false;
        this._releaseInFlight = null;
        this._startQueuedWhileReleasing = false;
        this._stoppingToast = null;

        // Stats
        this.totalAdded = 0;
        this.completed = 0;
        this.failed = 0;
        this.skipped = 0;
        this.lastFailures = [];

        // Callbacks
        this.onProgress = null;
        this.onComplete = null;
        this.onToast = null;

        this.isProcessing = false;
        this._rateLimitNotified = false;
        this._rateLimitUntilTs = 0;
        this._rateLimitTimer = null;
        this._rateLimitToast = null;
        this._skipToastShown = false;

        this._onPageExit = () => this._releaseSessionOnPageExit();
        if (typeof window !== 'undefined' && window.addEventListener) {
            window.addEventListener('pagehide', this._onPageExit);
            window.addEventListener('beforeunload', this._onPageExit);
        }
    }

    addFiles(files, libraryId) {
        if (this._abortBatch) {
            this._abortBatch = false;
            if (typeof this._resetBatchState === 'function') {
                this._resetBatchState();
            }
        }
        this.libraryId = libraryId;
        this.uploadUrl = `/upload-to-library/${libraryId}`;
        if (!this.isProcessing && this.queue.length === 0 && this.activeUploads === 0) {
            const restoredSessionId = this._loadPersistedSessionId();
            this.sessionId = restoredSessionId || this._newSessionId();
            this._persistSessionId(this.sessionId);
            this._sessionReleaseSent = false;
        }

        const newFiles = Array.from(files);
        this.queue.push(...newFiles);
        this.totalAdded += newFiles.length;
        this._notifyProgress();

        console.log(`[Queue] Added ${newFiles.length} files.`);
    }

    start() {
        if (this._releaseInFlight) {
            this._startQueuedWhileReleasing = true;
            return;
        }
        if (!this.isProcessing && this.queue.length > 0) {
            this.isProcessing = true;
            this._notifyProgress();
            this.processNext();
        }
    }

    processNext() {
        this.maxConcurrent = UPLOAD_QUEUE_CONFIG.MAX_CONCURRENT;
        if (this._abortBatch) {
            if (this.queue.length === 0 && this.activeUploads === 0) {
                this.finishBatch();
            }
            return;
        }
        if (this._rateLimitUntilTs && Date.now() < this._rateLimitUntilTs) {
            return;
        }

        if (this.queue.length === 0 && this.activeUploads === 0) {
            this.finishBatch();
            return;
        }

        while (this.activeUploads < this.maxConcurrent && this.queue.length > 0) {
            const file = this.queue.shift();
            this.uploadFile(file);
        }
    }

    uploadFile(file) {
        this.activeUploads++;
        this._notifyProgress();
        this._showActiveUpload(file);
        this._uploadFileInternal(file);
    }

    _notifyProgress() {
        if (!this.onProgress) return;
        const current = this.completed + this.failed + this.skipped;
        this.onProgress(current, this.totalAdded);
    }

    async _uploadFileInternal(file) {
        if (UPLOAD_LIMITS.MAX_SIZE_BYTES && file && file.size > UPLOAD_LIMITS.MAX_SIZE_BYTES) {
            const msg = UI_COPY.uploads.fileTooLarge(
                (file.size / (1024 * 1024)).toFixed(1),
                (UPLOAD_LIMITS.MAX_SIZE_BYTES / (1024 * 1024)).toFixed(1)
            );
            this._recordSkip(file, msg, 'size', {
                size_mb: Number((file.size / (1024 * 1024)).toFixed(2)),
                limit_mb: Number((UPLOAD_LIMITS.MAX_SIZE_BYTES / (1024 * 1024)).toFixed(2))
            });
            this.fileFinished();
            return;
        }

        let uploadFile = file;
        try {
            uploadFile = await resizeImageToFile(file);
        } catch (e) {
            const msg = (e && e.message) ? e.message : 'Upload skipped.';
            this._recordSkip(file, msg, 'pixels', {});
            this.fileFinished();
            return;
        }

        const formData = new FormData();
        const uploadName = (uploadFile && uploadFile.name) ? uploadFile.name : ((file && file.name) ? file.name : "tile.jpg");
        formData.append("file", uploadFile, uploadName);

        const xhr = new XMLHttpRequest();
        this._activeXhrs.add(xhr);
        let handled = false;
        const finalize = () => {
            if (handled) return;
            handled = true;
            this._activeXhrs.delete(xhr);
            this.fileFinished();
        };
        xhr.open("POST", this.uploadUrl, true);
        xhr.withCredentials = true;
        const csrfToken = API.getCsrfToken();
        if (csrfToken) {
            xhr.setRequestHeader("X-CSRF-Token", csrfToken);
        }
        if (this.sessionId) {
            xhr.setRequestHeader("X-Upload-Session", this.sessionId);
        }

        xhr.onload = () => {
            let resp = null;
            try {
                resp = JSON.parse(xhr.responseText);
            } catch (e) {
                resp = null;
            }

            if (xhr.status === 200) {
                if (resp && resp.status === 'ok') {
                    this.completed++;
                } else if (resp && resp.status === 'skipped') {
                    this.skipped++;
                    this._recordServerSkip(file, resp);
                } else {
                    this._recordFailure(file, xhr, resp);
                }
            } else {
                this._recordFailure(file, xhr, resp);
            }
            finalize();
        };

        xhr.onerror = () => {
            if (!this._abortBatch) {
                this._recordFailure(file, xhr, null);
            }
            finalize();
        };
        xhr.onabort = () => {
            finalize();
        };
        xhr.send(formData);
    }

    _recordFailure(file, xhr, payload) {
        const message =
            (payload && (payload.detail || payload.message || payload.error)) ||
            xhr.statusText ||
            "Upload failed";
        const failureClass = this._classifyFailure(xhr, payload, message);
        if (this._isSessionConflict(message, xhr && xhr.status, failureClass.errorCode)) {
            this.cancelBatch('session', message, { error_code: failureClass.errorCode || '' });
            return;
        }
        if (this._isHardLimitConflict(message, xhr && xhr.status, failureClass.errorCode)) {
            this.failed++;
            this.lastFailures.push({
                file: file && file.name ? file.name : "unknown",
                status: xhr.status || 0,
                message,
                kind: 'error',
                reason: failureClass.errorCode || 'limit'
            });
            this.cancelBatch('limit', message, { error_code: failureClass.errorCode || 'limit' });
            return;
        }

        if (this._abortBatch) return;
        this.failed++;
        if (failureClass.kind === 'rate_limit' && !this._rateLimitNotified) {
            this._rateLimitNotified = true;
            this._pauseForRateLimit(message, failureClass.retryAfter);
        }
        this.lastFailures.push({
            file: file && file.name ? file.name : "unknown",
            status: xhr.status || 0,
            message,
            kind: 'error',
            reason: failureClass.errorCode || ''
        });
    }

    _recordSkip(file, message, reason = 'unknown', meta = {}) {
        this.skipped++;
        const msg = message || 'Upload skipped.';
        if (!this._skipToastShown) {
            this._skipToastShown = true;
            showToast('Upload skipped', msg, 4000);
            API.logEvent('library_upload_precheck_skip', {
                reason,
                file_name: file && file.name ? file.name : 'unknown',
                ...meta
            }).catch(() => {});
        }
        this.lastFailures.push({
            file: file && file.name ? file.name : "unknown",
            status: 0,
            message: msg,
            kind: 'skipped',
            reason,
            details: msg
        });
    }

    _recordServerSkip(file, payload) {
        const reason = payload && payload.reason ? String(payload.reason) : '';
        const details = payload && (payload.details || payload.detail || payload.message || payload.error);
        const raw = details || reason || 'Upload skipped.';
        const msg = String(raw || 'Upload skipped.');
        this.lastFailures.push({
            file: file && file.name ? file.name : "unknown",
            status: 200,
            message: msg,
            kind: 'skipped',
            reason,
            details: details ? String(details) : ''
        });
    }

    fileFinished() {
        this.activeUploads--;
        if (this._abortBatch) {
            if (this.activeUploads <= 0) {
                this.finishBatch();
            }
            return;
        }
        this._notifyProgress();
        this.processNext();
    }

    finishBatch() {
        this.isProcessing = false;
        this._rateLimitNotified = false;
        this._clearRateLimitTimer();
        this._skipToastShown = false;
        this._closeActiveUploadToast();
        if (this._stoppingToast) {
            this._stoppingToast.close();
            this._stoppingToast = null;
        }
        const wasAbort = this._abortBatch;
        const abortReason = this._abortReason;
        const reportAbort = wasAbort && abortReason === 'user';
        const shouldRelease = !(wasAbort && abortReason === 'session');
        const shouldForceReleaseSessionConflict = (wasAbort && abortReason === 'session');
        if (shouldRelease || shouldForceReleaseSessionConflict) {
            const forceRelease = reportAbort || shouldForceReleaseSessionConflict;
            this._releaseSession(forceRelease).catch(() => {});
        }
        this.sessionId = null;

        // 1. Notify UI (Updates Grid & Counts immediately)
        if (wasAbort && abortReason === 'session') {
            this._resetBatchState();
            return;
        }
        if (this.onComplete) {
            this.onComplete({
                total: this.totalAdded,
                completed: this.completed,
                skipped: this.skipped,
                errors: this.failed,
                error_details: this.lastFailures.slice(),
                cancelled: reportAbort,
                abort_reason: abortReason || '',
            });
        }

        // 2. Trigger Analysis & Poll
        if (this.libraryId && this.completed > 0) {
            this.triggerAnalysis(this.libraryId, {
                total: this.totalAdded,
                completed: this.completed,
                skipped: this.skipped,
                errors: this.failed
            });
        }

        // 3. Reset
        this._resetBatchState();
    }

    _clearRateLimitTimer() {
        if (this._rateLimitTimer) {
            clearInterval(this._rateLimitTimer);
            this._rateLimitTimer = null;
        }
    }

    _pauseForRateLimit(message, retryAfter) {
        const base = this._stripRetryHint(message);
        const retrySeconds = (retryAfter && retryAfter > 0) ? Math.ceil(retryAfter) : 5;
        const until = Date.now() + (retrySeconds * 1000);
        if (until <= this._rateLimitUntilTs) return;

        this._rateLimitUntilTs = until;
        if (!this._rateLimitToast) {
            this._rateLimitToast = showToast('Uploads paused', '', 0);
        }

        const update = () => {
            const remaining = Math.max(0, Math.ceil((this._rateLimitUntilTs - Date.now()) / 1000));
            if (remaining <= 0) {
                this._rateLimitUntilTs = 0;
                this._clearRateLimitTimer();
                this._rateLimitNotified = false;
                if (this._rateLimitToast) {
                    this._rateLimitToast.close();
                    this._rateLimitToast = null;
                }
                showToast('Uploads resumed', 'Uploads resumed.', 3000);
                this.processNext();
                return;
            }
            const msg = `${base} Try again in ${remaining}s.`;
            if (this._rateLimitToast) {
                this._rateLimitToast.updateMessage(msg);
            }
        };

        update();
        this._clearRateLimitTimer();
        this._rateLimitTimer = setInterval(update, 1000);
    }

    _stripRetryHint(message) {
        const text = String(message || '');
        const marker = ' Try again in ';
        if (!text.includes(marker)) return text || 'Upload rate limit reached.';
        return text.split(marker)[0].trim() || 'Upload rate limit reached.';
    }

    _classifyFailure(xhr, payload, message) {
        const status = Number((xhr && xhr.status) || 0);
        const retryAfterRaw = xhr ? xhr.getResponseHeader('Retry-After') : null;
        const retryAfter = (retryAfterRaw != null && retryAfterRaw !== '') ? Number(retryAfterRaw) : null;
        const errorCode = this._normalizeUploadErrorCode(
            (xhr && (xhr.getResponseHeader('X-Upload-Error') || xhr.getResponseHeader('x-upload-error'))) ||
            (payload && payload.error_code) ||
            (payload && payload.detail && payload.detail.error_code)
        );
        if (this._isSessionConflict(message, status, errorCode)) {
            return { kind: 'session', retryAfter, errorCode };
        }
        if (this._isHardLimitConflict(message, status, errorCode)) {
            return { kind: 'limit', retryAfter, errorCode };
        }
        if (this._isRetryableRateLimit(message, status, retryAfter, errorCode)) {
            return { kind: 'rate_limit', retryAfter, errorCode };
        }
        return { kind: 'error', retryAfter, errorCode };
    }

    _normalizeUploadErrorCode(value) {
        const code = String(value || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9_]+/g, '_')
            .replace(/^_+|_+$/g, '');
        return code || '';
    }

    _isSessionConflict(message, status, errorCode = '') {
        const text = String(message || '');
        if (errorCode === 'session_active' || errorCode === 'session_missing') return true;
        if (status === 429 && text.includes('Another library upload is already in progress')) return true;
        if (status === 400 && text.includes('Upload session missing')) return true;
        return false;
    }

    _isHardLimitConflict(message, status, errorCode = '') {
        const text = String(message || '');
        if (errorCode === 'tile_limit' || errorCode === 'storage_limit') return true;
        if (status === 429 && text.includes('Library tile limit reached')) return true;
        if (status === 429 && text.includes('Storage limit reached')) return true;
        return false;
    }

    _isRetryableRateLimit(message, status, retryAfter, errorCode = '') {
        if (status !== 429) return false;
        if (!(Number.isFinite(retryAfter) && Number(retryAfter) > 0)) return false;
        if (!errorCode) return true;
        if (errorCode === 'rate_limit') return true;
        if (errorCode === 'rate_limit_bytes') return true;
        if (errorCode === 'rate_limit_backend_unavailable') return true;
        if (errorCode === 'rate_limit_bytes_backend_unavailable') return true;
        return false;
    }

    cancelBatch(reason = 'user', message = '', meta = {}) {
        if (this._abortBatch) return;
        const hadPending = (this.activeUploads > 0 || this.queue.length > 0);
        this._abortBatch = true;
        this._abortReason = reason;
        this._abortMeta = meta || {};
        this.queue = [];
        this._clearRateLimitTimer();
        this._rateLimitUntilTs = 0;
        this._rateLimitNotified = false;
        if (this._rateLimitToast) {
            this._rateLimitToast.close();
            this._rateLimitToast = null;
        }
        this._closeActiveUploadToast();

        if (reason === 'session') {
            const toastTitle = 'Upload blocked';
            const toastMessage = message || 'Another library upload is already in progress.';
            showToast(toastTitle, toastMessage, 5000);
        } else if (reason === 'limit') {
            const toastTitle = 'Upload limit reached';
            const toastMessage = message || 'No more photos can be uploaded on this plan.';
            showToast(toastTitle, toastMessage, 6000);
        } else if (reason === 'user' && hadPending) {
            this._stoppingToast = showToast('Stopping upload', 'Cancelling in-flight uploads…', 0);
        }

        if (this.onProgress) {
            this.onProgress(0, 0);
        }

        try {
            this._activeXhrs.forEach((xhr) => {
                try { xhr.abort(); } catch (e) {}
            });
        } catch (e) {}
        this._activeXhrs.clear();

        API.logEvent('library_upload_cancelled', {
            reason,
            library_id: this.libraryId || '',
            total_added: this.totalAdded,
            active_uploads: this.activeUploads,
            ...this._abortMeta,
        }).catch(() => {});
        const outcome =
            reason === 'limit' ? 'limit_reached'
            : reason === 'session' ? 'session_conflict'
            : reason === 'user' ? 'user_cancelled'
            : 'stopped';
        API.logUiFlow('library_upload', 'stop', outcome, {
            reason,
            library_id: this.libraryId || '',
            total_added: this.totalAdded,
            active_uploads: this.activeUploads,
            ...this._abortMeta,
        }).catch(() => {});

        if (this.activeUploads <= 0) {
            this.finishBatch();
        }
    }

    _showActiveUpload(file) {
        const name = file && file.name ? file.name : 'photo';
        const msg = `Currently uploading: ${name}`;
        if (!this._activeUploadToast) {
            this._activeUploadToast = showToast('Uploading photos', msg, 0);
        } else {
            this._activeUploadToast.updateMessage(msg);
        }
    }

    _closeActiveUploadToast() {
        if (this._activeUploadToast) {
            this._activeUploadToast.close();
            this._activeUploadToast = null;
        }
    }

    _resetBatchState() {
        this._abortBatch = false;
        this._abortReason = '';
        this._abortMeta = {};
        this._sessionReleaseSent = false;
        this.queue = [];
        this.totalAdded = 0;
        this.completed = 0;
        this.failed = 0;
        this.skipped = 0;
        this.lastFailures = [];
    }

    _releaseSession(force = false, options = {}) {
        const keepalive = Boolean(options && options.keepalive);
        const sessionId = String((options && options.sessionId) || this.sessionId || '');
        if (!sessionId) return Promise.resolve({ status: 'ok', released: false, skipped: true });
        if (this._sessionReleaseSent && !force) {
            return Promise.resolve({ status: 'ok', released: false, skipped: true });
        }
        this._sessionReleaseSent = true;
        const releasePromise = API.releaseLibraryUploadSession(sessionId, force, { keepalive })
            .then((result) => {
                this._clearPersistedSessionId(sessionId);
                return result;
            })
            .catch((error) => {
                this._sessionReleaseSent = false;
                throw error;
            })
            .finally(() => {
                this._releaseInFlight = null;
                if (this._startQueuedWhileReleasing && !this.isProcessing && this.queue.length > 0) {
                    this._startQueuedWhileReleasing = false;
                    this.start();
                } else if (this.queue.length === 0) {
                    this._startQueuedWhileReleasing = false;
                }
            });
        this._releaseInFlight = releasePromise;
        return releasePromise;
    }

    _releaseSessionOnPageExit() {
        const sessionId = this.sessionId || this._loadPersistedSessionId();
        if (!sessionId) return;
        this._releaseSession(false, { keepalive: true, sessionId }).catch(() => {});
    }

    _persistSessionId(sessionId) {
        const safeSessionId = String(sessionId || '').trim();
        if (!safeSessionId) return;
        if (typeof window === 'undefined' || !window.sessionStorage) return;
        try {
            window.sessionStorage.setItem(LIBRARY_UPLOAD_SESSION_STORAGE_KEY, safeSessionId);
        } catch (e) {}
    }

    _loadPersistedSessionId() {
        if (typeof window === 'undefined' || !window.sessionStorage) return '';
        try {
            const raw = window.sessionStorage.getItem(LIBRARY_UPLOAD_SESSION_STORAGE_KEY) || '';
            const safe = String(raw || '').trim();
            if (!safe || safe.length > 200) return '';
            return safe;
        } catch (e) {
            return '';
        }
    }

    _clearPersistedSessionId(expectedSessionId = '') {
        if (typeof window === 'undefined' || !window.sessionStorage) return;
        try {
            const current = String(window.sessionStorage.getItem(LIBRARY_UPLOAD_SESSION_STORAGE_KEY) || '').trim();
            const expected = String(expectedSessionId || '').trim();
            if (expected && current && current !== expected) return;
            window.sessionStorage.removeItem(LIBRARY_UPLOAD_SESSION_STORAGE_KEY);
        } catch (e) {}
    }

    triggerAnalysis(libId, summary) {
        // Show persistent toast (ms=0 means it won't close automatically)
        const processingToast = TOASTS.libraryProcessing;
        const toast = showToast(processingToast.title, processingToast.message, processingToast.duration);

        const headers = { 'Content-Type': 'application/json' };
        const csrfToken = API.getCsrfToken();
        if (csrfToken) headers['x-csrf-token'] = csrfToken;

        fetch(`/library/${libId}/analyze`, {
            method: 'POST',
            credentials: 'include',
            headers,
            body: JSON.stringify(summary || {})
        })
        .then(res => {
            if (res.ok) {
                // Wait 1s for backend to set Redis key, then start polling
                setTimeout(() => this.pollForCompletion(libId, toast), UPLOAD_QUEUE_CONFIG.ANALYSIS_START_DELAY_MS);
            } else {
                if (toast) toast.close();
                return res.json().then(payload => {
                    const message = (payload && (payload.detail || payload.message || payload.error)) || 'Analysis request denied.';
                    const retryAfter = res.headers.get('Retry-After');
                    const retryMsg = retryAfter ? ` Try again in ${retryAfter}s.` : '';
                    showToast('Analysis limit reached', `${message}${retryMsg}`, 4000);
                }).catch(() => {
                    showToast('Analysis limit reached', 'Analysis request denied.', 4000);
                });
            }
        })
        .catch(err => {
            console.error("Signal Error:", err);
            if (toast) toast.close();
        });
    }

    pollForCompletion(libId, toast) {
        const interval = setInterval(() => {
            fetch(`/library/${libId}/analysis-status`, { credentials: 'include' })
                .then(r => r.json())
                .then(data => {
                    // Status 'idle' means the Worker finished and deleted the key
                    if (data.status === ANALYSIS_STATUS.IDLE) {
                        clearInterval(interval);

                        if (toast) {
                            // UPDATE MESSAGE TO DONE
                            toast.updateMessage(TOASTS.libraryOptimized.message);

                            // Close automatically after 5 seconds
                            setTimeout(() => {
                                toast.close();
                            }, UPLOAD_QUEUE_CONFIG.ANALYSIS_TOAST_CLOSE_DELAY_MS);
                        }
                    }
                })
                .catch(e => {
                    clearInterval(interval); // Stop polling on network error
                });
        }, UPLOAD_QUEUE_CONFIG.ANALYSIS_POLL_INTERVAL_MS); // Poll every 1 second
    }

    _newSessionId() {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        return `sess_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }
}
